import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Steps, Switch, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

const { Title, Text } = Typography;

type AdminOrganizationRow = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  createdAt: string;
  manualInvoicing?: boolean;
  _count?: {
    members: number;
    subscriptions: number;
  };
  activeSubscriptionCount?: number;
};

type AdminAppPrice = {
  id: string;
  name: string | null;
  interval: string;
  unitAmount: number | null;
  currency: string;
};

type AdminApp = {
  isCatalogGroup?: boolean;
  parent?: { isCatalogGroup?: boolean } | null;
  id: string;
  name: string;
  slug: string;
  parentId?: string | null;
  isActive?: boolean;
  prices?: AdminAppPrice[];
};

export default function AdminOrganizationsPage() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<AdminOrganizationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [apps, setApps] = useState<AdminApp[]>([]);
  const [creating, setCreating] = useState(false);
  const [defaultTrialDays, setDefaultTrialDays] = useState<number | undefined>();
  const [orgForm] = Form.useForm();
  const [subscriptionsForm] = Form.useForm();
  const [invitesForm] = Form.useForm();
  const [createdOrg, setCreatedOrg] = useState<AdminOrganizationRow | null>(null);

  const loadOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.admin.listOrganizations();
      setOrganizations(data.organizations || []);
    } catch (error: any) {
      message.error(error.message || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  useEffect(() => {
    if (wizardOpen) {
      loadApps();
      loadAdminConfig();
    }
  }, [wizardOpen]);

  const handleDeleteOrganization = useCallback(async (orgId: string) => {
    try {
      setLoading(true);
      await api.admin.deleteOrganization(orgId);
      message.success('Organization deleted');
      await loadOrganizations();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete organization');
    } finally {
      setLoading(false);
    }
  }, [loadOrganizations]);

  const loadApps = async () => {
    try {
      const data = await api.admin.listApps();
      const appList: AdminApp[] = (data.apps || []).filter(
        (app: AdminApp) => !app.isCatalogGroup && (!app.parentId || app.parent?.isCatalogGroup) && app.isActive !== false
      );
      setApps(appList);
    } catch (error: any) {
      message.error(error.message || 'Failed to load apps');
    }
  };

  const loadAdminConfig = async () => {
    try {
      const data = await api.admin.getConfig();
      if (typeof data?.stripeTrialDays === 'number') {
        setDefaultTrialDays(data.stripeTrialDays);
      }
    } catch {
      // Optional config; keep default empty.
    }
  };

  useEffect(() => {
    if (!wizardOpen || defaultTrialDays === undefined) return;
    const current = subscriptionsForm.getFieldValue('subscriptions') as Array<Record<string, unknown>> | undefined;
    if (!current || current.length === 0) {
      subscriptionsForm.setFieldsValue({
        subscriptions: [{ quantity: 1, trialDays: defaultTrialDays }],
      });
      return;
    }
    const patched = current.map((row) =>
      row.trialDays === undefined ? { ...row, trialDays: defaultTrialDays } : row
    );
    subscriptionsForm.setFieldsValue({ subscriptions: patched });
  }, [defaultTrialDays, subscriptionsForm, wizardOpen]);

  const resetWizard = () => {
    setWizardStep(0);
    setCreatedOrg(null);
    orgForm.resetFields();
    subscriptionsForm.resetFields();
    invitesForm.resetFields();
  };

  const openWizard = () => {
    resetWizard();
    setWizardOpen(true);
  };

  const handleCreateOrg = async (values: {
    name: string;
    description?: string;
    slug?: string;
    manualInvoicing?: boolean;
  }) => {
    try {
      setCreating(true);
      const payload = {
        ...values,
        manualInvoicing: Boolean(values.manualInvoicing),
      };
      const data = await api.admin.createOrganization(payload);
      setCreatedOrg(data.organization);
      message.success('Organization created');
      setWizardStep(1);
      await loadOrganizations();
    } catch (error: any) {
      message.error(error.message || 'Failed to create organization');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateSubscriptions = async (values: { subscriptions?: Array<{ appId?: string; priceId?: string; quantity?: number; trialDays?: number; trialOnly?: boolean }> }) => {
    if (!createdOrg) return;
    const rows = values.subscriptions || [];
    const validRows = rows.filter((row) => row.appId && (row.trialOnly || row.priceId));
    if (validRows.length === 0) {
      message.warning('Add at least one subscription');
      return;
    }
    try {
      setCreating(true);
      for (const row of validRows) {
        await api.admin.createOrganizationSubscription(createdOrg.id, {
          appId: row.appId!,
          priceId: row.priceId,
          quantity: row.quantity,
          trialDays: row.trialDays,
          trialOnly: Boolean(row.trialOnly),
        });
      }
      message.success('Trial subscriptions created');
      setWizardStep(2);
      await loadOrganizations();
    } catch (error: any) {
      message.error(error.message || 'Failed to create trial subscriptions');
    } finally {
      setCreating(false);
    }
  };

  const handleInviteUsers = async (values: { emails?: string }) => {
    if (!createdOrg) return;
    const raw = values.emails || '';
    const emails = raw
      .split(/[\n,;]+/)
      .map((value) => value.trim())
      .filter(Boolean);
    if (emails.length === 0) {
      message.warning('Add at least one email');
      return;
    }
    try {
      setCreating(true);
      await api.admin.inviteOrganizationUsers(createdOrg.id, emails);
      message.success('Invitations sent');
      setWizardOpen(false);
      resetWizard();
      await loadOrganizations();
    } catch (error: any) {
      message.error(error.message || 'Failed to send invitations');
    } finally {
      setCreating(false);
    }
  };

  const columns: ColumnsType<AdminOrganizationRow> = useMemo(
    () => [
      {
        title: 'Organization',
        dataIndex: 'name',
        key: 'name',
        render: (_value, record) => (
          <Space direction="vertical" size={0}>
            <Button type="link" onClick={() => navigate(`/admin/organizations/${record.id}`)}>
              {record.name}
            </Button>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.slug}
            </Text>
          </Space>
        ),
      },
      {
        title: 'Members',
        dataIndex: '_count',
        key: 'members',
        render: (count) => count?.members ?? 0,
      },
      {
        title: 'Billing',
        key: 'billing',
        render: (_value, record) =>
          record.manualInvoicing ? (
            <Tag color="purple">Manual</Tag>
          ) : (
            <Tag color="green">Stripe</Tag>
          ),
      },
      {
        title: 'Subscriptions',
        key: 'subscriptions',
        render: (_value, record) => {
          const total = record._count?.subscriptions ?? 0;
          const active = record.activeSubscriptionCount ?? 0;
          return (
            <div>
              <Text strong>{active} active</Text>
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                {total} total
              </Text>
            </div>
          );
        },
      },
      {
        title: 'Created',
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (value) => new Date(value).toLocaleDateString(),
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_value, record) => (
          <Space>
            <Button onClick={() => navigate(`/admin/organizations/${record.id}`)}>
              Manage
            </Button>
            {record.slug === 'annea' && <Tag color="gold">Annea</Tag>}
            {record.slug !== 'annea' && (
              <Popconfirm
                title="Delete organization?"
                description="Only possible when there are no active subscriptions."
                onConfirm={() => handleDeleteOrganization(record.id)}
                okText="Delete"
                okButtonProps={{ danger: true }}
              >
                <Button danger>Delete</Button>
              </Popconfirm>
            )}
          </Space>
        ),
      },
    ],
    [handleDeleteOrganization, navigate]
  );

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2}>Admin: Organizations</Title>
          <Text type="secondary">Manage organizations, members, and access</Text>
        </div>
        <Button type="primary" onClick={openWizard}>
          Create Organization
        </Button>
      </div>

      <Table
        rowKey="id"
        dataSource={organizations}
        columns={columns}
        loading={loading}
      />

      <Modal
        title="Create Organization"
        open={wizardOpen}
        onCancel={() => {
          setWizardOpen(false);
          resetWizard();
        }}
        footer={null}
        width={760}
      >
        <Steps
          current={wizardStep}
          items={[
            { title: 'Organization' },
            { title: 'Trial Subscriptions' },
            { title: 'Invite Users' },
          ]}
          style={{ marginBottom: 24 }}
        />

        {wizardStep === 0 && (
          <Form
            form={orgForm}
            layout="vertical"
            onFinish={handleCreateOrg}
          >
            <Form.Item
              name="name"
              label="Organization Name"
              rules={[{ required: true, message: 'Name is required' }]}
            >
              <Input placeholder="Customer name" />
            </Form.Item>
            <Form.Item name="slug" label="Slug (optional)">
              <Input placeholder="Leave blank to auto-generate" />
            </Form.Item>
            <Form.Item name="description" label="Description (optional)">
              <Input.TextArea rows={3} placeholder="Short description" />
            </Form.Item>
            <Form.Item
              name="manualInvoicing"
              label="Manual invoicing"
              valuePropName="checked"
              tooltip="Disable Stripe billing for this organization"
            >
              <Switch />
            </Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={creating}>
                Create
              </Button>
            </Space>
          </Form>
        )}

        {wizardStep === 1 && (
          <Form
            form={subscriptionsForm}
            layout="vertical"
            onFinish={handleCreateSubscriptions}
            initialValues={{
              subscriptions: [
                {
                  quantity: 1,
                  trialDays: defaultTrialDays,
                },
              ],
            }}
          >
            <Form.List name="subscriptions">
              {(fields, { add, remove }) => (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {fields.map((field) => (
                    <Form.Item
                      key={field.key}
                      noStyle
                      shouldUpdate={(prev, current) =>
                        prev.subscriptions?.[field.name]?.appId !==
                        current.subscriptions?.[field.name]?.appId ||
                        prev.subscriptions?.[field.name]?.trialOnly !==
                        current.subscriptions?.[field.name]?.trialOnly
                      }
                    >
                      {({ getFieldValue }) => {
                        const appId = getFieldValue([
                          'subscriptions',
                          field.name,
                          'appId',
                        ]) as string | undefined;
                        const trialOnly = Boolean(
                          getFieldValue(['subscriptions', field.name, 'trialOnly'])
                        );
                        const app = apps.find((item) => item.id === appId);
                        const priceOptions = (app?.prices || []).map((price) => ({
                          value: price.id,
                          label: `${price.name || price.interval} (${price.currency.toUpperCase()}${price.unitAmount ? ' ' + price.unitAmount / 100 : ''})`,
                        }));
                        return (
                      <Space key={field.key} align="start" style={{ width: '100%' }}>
                        <Form.Item
                          {...field}
                          name={[field.name, 'appId']}
                          label="App"
                          rules={[{ required: true, message: 'App is required' }]}
                        >
                          <Select
                            placeholder="Select app"
                            style={{ width: 200 }}
                            options={apps.map((appOption) => ({
                              value: appOption.id,
                              label: appOption.name,
                            }))}
                            onChange={() =>
                              subscriptionsForm.setFieldValue(
                                ['subscriptions', field.name, 'priceId'],
                                undefined
                              )
                            }
                          />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          name={[field.name, 'priceId']}
                          label="Price"
                          rules={trialOnly ? [] : [{ required: true, message: 'Price is required' }]}
                        >
                          <Select
                            placeholder={trialOnly ? 'Not required for trial-only' : 'Select price'}
                            style={{ width: 220 }}
                            options={priceOptions}
                            disabled={trialOnly || priceOptions.length === 0}
                          />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          name={[field.name, 'trialOnly']}
                          label="Mode"
                          valuePropName="checked"
                          initialValue={false}
                        >
                          <Switch checkedChildren="Trial Only" unCheckedChildren="Paid After Trial" />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          name={[field.name, 'quantity']}
                          label="Seats"
                        >
                          <InputNumber min={1} placeholder="1" />
                        </Form.Item>
                        <Form.Item
                          {...field}
                          name={[field.name, 'trialDays']}
                          label="Trial days"
                        >
                          <InputNumber min={1} placeholder="Default" />
                        </Form.Item>
                        <Button danger onClick={() => remove(field.name)}>
                          Remove
                        </Button>
                      </Space>
                        );
                      }}
                    </Form.Item>
                  ))}
                  <Button onClick={() => add({ quantity: 1, trialDays: defaultTrialDays })}>
                    Add Subscription
                  </Button>
                </Space>
              )}
            </Form.List>

            <Space style={{ marginTop: 16 }}>
              <Button onClick={() => setWizardStep(2)}>Skip</Button>
              <Button type="primary" htmlType="submit" loading={creating}>
                Create Trials
              </Button>
            </Space>
          </Form>
        )}

        {wizardStep === 2 && (
          <Form
            form={invitesForm}
            layout="vertical"
            onFinish={handleInviteUsers}
          >
            <Form.Item
              name="emails"
              label="Invite users (comma or newline separated)"
            >
              <Input.TextArea rows={4} placeholder="user1@example.com, user2@example.com" />
            </Form.Item>
            <Space>
              <Button onClick={() => {
                setWizardOpen(false);
                resetWizard();
              }}>
                Close
              </Button>
              <Button type="primary" htmlType="submit" loading={creating}>
                Send Invites
              </Button>
            </Space>
          </Form>
        )}
      </Modal>
    </Space>
  );
}
